import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { HQConfig } from '../utils/geoUtils';

interface HQContextProps {
  hq: HQConfig | null;
  isLoading: boolean;
  reload: () => Promise<void>;
}

const HQContext = createContext<HQContextProps | undefined>(undefined);

export const HQProvider = ({ children }: { children: ReactNode }) => {
  const [hq, setHQ] = useState<HQConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'hq')
      .maybeSingle();
    setHQ(data?.value ?? null);
    setIsLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) load();
      else setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        (async () => { await load(); })();
      } else {
        setHQ(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <HQContext.Provider value={{ hq, isLoading, reload: load }}>
      {children}
    </HQContext.Provider>
  );
};

export const useHQ = (): HQContextProps => {
  const ctx = useContext(HQContext);
  if (!ctx) throw new Error('useHQ must be used within HQProvider');
  return ctx;
};
